import { 
    useState, 
    useEffect, 
    useLayoutEffect,
    useReducer,
    useRef,
    useMemo,
    useCallback,
    useContext,
    createContext,
    Fragment,
    forwardRef
} from 'react';

import { 
    motion,
    useAnimation,
    useAnimationControls,
    AnimatePresence, 
    usePresence,
    transform,
    useMotionValue,
    useMotionValueEvent
} from 'framer-motion';


import { 
    CloseButton,
    modalContext 
} from './view.jsx';


const galleryContext = createContext()


const GalleryCurrentItem = ( {
    children,
    ready,
    setReady,
    currentWidth,
    setCurrentWidth
} ) => {
    const imageRef = useRef()
    const [loaded, setLoaded] = useState( false )

    const {
        content: {
            ref: modalContentRef
        },
        closeButton: {
            ref: closeButtonRef,
            ready: modalReady,
            setClose: modalClose
        }
    } = useContext( modalContext )

    const { 
        hasTitle,
        hasCaption,
        rate
    } = useContext( galleryContext )

    const {
        title,
        caption,
        source_url: url,
        media_details: {
            height,
            width
        }
    } = children || {}


    function getNodeWidth() {
        const { 
            width: imageWidth,
            height: imageHeight
        } = imageRef.current.getBoundingClientRect()


        if ( !ready )
            return imageWidth;

        let { 
            width: modalWidth,
            height: modalHeight
        } = modalContentRef.current.getBoundingClientRect()

        const {
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft
        } = getComputedStyle( modalContentRef.current )

        if ( paddingRight || paddingLeft )
            modalWidth = modalWidth - parseFloat( paddingRight ) - parseFloat( paddingLeft )

        if ( paddingTop || paddingBottom )
            modalHeight = modalHeight - parseFloat( paddingTop ) - parseFloat( paddingBottom )

        // for small media
        if ( height < modalHeight && width < modalWidth )
            return width;


        const aspectRatio = width / height // get aspect ratio
        let newWidth = modalHeight * aspectRatio


        return newWidth <= modalWidth ? newWidth : modalWidth;
    }


    useEffect( () => {
        if ( loaded ) {
            const imageWidth = getNodeWidth()

            if ( ready ) {
                setCurrentWidth( {
                    width: imageWidth,
                    auto: false
                } )
            }

            if ( !ready ) {
                setReady( true )
            }
        }
    }, [loaded] )


    return (
        <motion.div 
            className="wp-block-cwc-gallery__gallery-item is-current"
            animate={ {
                opacity: loaded ? 1 : 0
            } }
            exit={ {
                opacity: 0
            } }
            initial={ {
                opacity: 0
            } }
            transition={ {
                duration: rate
            } }
        >
            <figure 
                className="wp-block-cwc-gallery__gallery-item-inner"
                style={ {
                    width: !currentWidth.auto ? `${currentWidth.width}px` : 'auto'
                } }
            >
                <img
                    ref={ imageRef }
                    src={ url }
                    height={ height }
                    width={ width }
                    loading="eager"
                    onLoad={ () => setLoaded( true ) }
                />

                { ( hasTitle || hasCaption ) && 
                    <figcaption>
                        { hasTitle && title.rendered && 
                            <h4 
                                dangerouslySetInnerHTML={ { __html: title.rendered } } 
                            />
                        }
                        { hasCaption && caption.rendered && 
                            <div 
                                className="has-small-font-size" 
                                dangerouslySetInnerHTML={ { __html: caption.rendered } } 
                            />
                        }
                    </figcaption>
                }
            </figure>

            <CloseButton
                ref={ closeButtonRef }
                ready={ modalReady }
                setClose={ modalClose }
            />
        </motion.div>
    )
}


const Thumbnail = ( {
    children,
    position,
    containerNode,
    currentWidth,
    setCurrentWidth,
    setChangeItem
} ) => {
    const [loaded, setLoaded] = useState( false )

    const { source_url: url } = children || {}

    const { rate } = useContext( galleryContext )


    function changeItem() {
        const { width: containerWidth } = containerNode.getBoundingClientRect()

        setCurrentWidth( {
            width: containerWidth,
            auto: false
        } )

        setChangeItem( children.id )
    }


    return (
        <div className={ `wp-block-cwc-gallery__gallery-item${position ? ` is-${position}` : ''}` }>
            <motion.figure 
                className="wp-block-cwc-gallery__gallery-item-inner"
                onClick={ e => changeItem() }
                animate={ {
                    opacity: loaded ? 1 : 0
                } }
                exit={ {
                    opacity: 0
                } }
                initial={ {
                    opacity: 0
                } }
                transition={ {
                    duration: rate
                } }
            >
                <img
                    src={ url }
                    loading="eager"
                    onLoad={ () => setLoaded( true ) }
                />
            </motion.figure>
        </div>
    )
}


function GalleryItems( {
    children,
    startedId
} ) {
    const ref = useRef()
    const [currentItemId, setCurrentItemId] = useState( startedId )
    const [ready, setReady] = useState( false )
    const [changeItem, setChangeItem] = useState()
    const [currentWidth, setCurrentWidth] = useState( {
        width: null,
        auto: true
    } )

    const controls = useAnimationControls()

    const { rate } = useContext( galleryContext )

    
    const currentIndex = useMemo( () => children.findIndex( item => item.id == currentItemId ), [currentItemId] )
    let siblingsIndexes = {}

    if ( currentIndex > 0 )
        siblingsIndexes.previous = currentIndex - 1

    if ( currentIndex < children.length - 1 )
        siblingsIndexes.next = currentIndex + 1


    //console.log(currentWidth.current)


    /**
     * Toggles gallery items when you press the arrow keys on your keyboard.
     * @param {event} event - onKeyDown event.
     */
    const setCurrentItemKey = useCallback( event => {
        if ( ready ) {
            let id
            if ( event.code === 'ArrowLeft' && siblingsIndexes?.previous !== undefined ) {
                id = children[siblingsIndexes.previous].id
            }

            if ( event.code === 'ArrowRight' && siblingsIndexes?.next !== undefined ) {
                id = children[siblingsIndexes.next].id
            }

            if ( id ) {
                const { width: containerWidth } = ref.current.getBoundingClientRect()

                setCurrentWidth( {
                    width: containerWidth,
                    auto: false
                } )
        
                setChangeItem( id )
            }
        }
    }, [currentItemId, ready] )


    useEffect( () => {
        document.addEventListener( 'keydown', setCurrentItemKey )
        
        return () => document.removeEventListener( 'keydown', setCurrentItemKey )
    }, [setCurrentItemKey] )


    useEffect( () => {
        if ( currentWidth.auto ) {
            controls.set( {
                width: 'auto'
            } )
        } else {
            controls.start( {
                width: `${currentWidth.width}px`
            } )
        }
    }, [currentWidth] )


    useEffect( () => {
        if ( changeItem ) {
            setCurrentItemId( changeItem )
        }
    }, [changeItem] )


    return (
        <div 
            ref={ ref }
            className={ `wp-block-cwc-gallery__gallery-items${!currentWidth.auto ? ' is-processing' : ''}` }
        >
            <AnimatePresence 
                initial={ false }
                custom={ currentWidth }
            >
                { ready && siblingsIndexes?.previous !== undefined &&
                    <Thumbnail
                        containerNode={ ref.current }
                        key={ `${children[siblingsIndexes.previous].id}-previous` }
                        position="previous"
                        currentItemId={ currentItemId }
                        currentWidth={ currentWidth }
                        setCurrentWidth={ setCurrentWidth }
                        setChangeItem={ setChangeItem }
                    >
                        { children[siblingsIndexes.previous] }
                    </Thumbnail>
                }

                { ready && siblingsIndexes?.next !== undefined &&
                    <Thumbnail
                        containerNode={ ref.current }
                        key={ `${children[siblingsIndexes.next].id}-next` }
                        position="next"
                        currentItemId={ currentItemId }
                        currentWidth={ currentWidth }
                        setCurrentWidth={ setCurrentWidth }
                        setChangeItem={ setChangeItem }
                    >
                        { children[siblingsIndexes.next] }
                    </Thumbnail>
                }
            </AnimatePresence>


            <motion.div 
                className="wp-block-cwc-gallery__gallery-items-spacer"
                layout
                animate={ controls }
                initial={ {
                    width: 'auto'
                } }
                transition={ {
                    duration: rate,
                    delay: 0.1
                } }
                onAnimationComplete={ definition => {
                    setTimeout( () => {
                        setCurrentWidth( {
                            width: null,
                            auto: true
                        } )
                        setChangeItem( undefined )
                    }, 150 )
                } }
            >
                <AnimatePresence 
                    initial={ false }
                    custom={ currentWidth }
                >
                    <GalleryCurrentItem
                        key={ `${children[currentIndex].id}-current` }
                        ready={ ready }
                        setReady={ setReady }
                        currentWidth={ currentWidth }
                        setCurrentWidth={ setCurrentWidth }
                    >
                        { children[currentIndex] }
                    </GalleryCurrentItem>
                </AnimatePresence>
            </motion.div>
        </div>
    )
}


function GalleryLoader(props) {

    return (
        <svg className="wp-block-cwc-gallery__gallery-loader" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 100 100" enable-background="new 0 0 0 0">
            <path fill="#cccccc" d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                <animateTransform 
                    attributeName="transform" 
                    attributeType="XML" 
                    type="rotate"
                    dur="500ms" 
                    from="0 50 50"
                    to="360 50 50" 
                    repeatCount="indefinite" 
                />
            </path>
        </svg>
    )
}



export default function Gallery( {
    ids,
    startedId,
    hasTitle,
    hasCaption,
    rate = 0.4
} ) {
    const [loading, setLoading] = useState( false )
    const [images, setImages] = useState( [] )

    const { 
        closeButton: {
            setClose: modalClose
        }
    } = useContext( modalContext )


    async function getImages() {
        let url = new URL( `${cwcGalleryParams.restUrl}media` )
        const params = {
            'per_page'  : '100',
            'orderby'   : 'include',
            'media_type': 'image',
            'include'   : ids
        }

        // set query params
        for (const prop in params) {
            if ( params.hasOwnProperty( prop ) ) {
                url.searchParams.set( prop, params[prop] )
            }
        }

        const response = await fetch( new URL( url.toString() ) )
        const result = await response.json()

        if ( !!result.length ) {
            setImages( result )
        } else {
            modalClose()
        }

        setLoading( false )
    }

    useEffect( () => {
        if ( ids.length ) {
            setLoading( true )
            getImages()
        }
    }, [] )


    return (
        <galleryContext.Provider value={ {
            hasTitle,
            hasCaption,
            rate
        } }>
            { !!images.length &&
                <GalleryItems
                    startedId={ startedId }
                >
                    { images }
                </GalleryItems>
            }

            { loading &&
                <GalleryLoader />
            }
        </galleryContext.Provider>
    )
}